import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';
import { ArtifactsDataProvider } from '@trovata/app/features/ai-artifacts/services/artifacts-data-provider';
import {
	ArtifactDataResponse,
	ArtifactSaveBody,
	ArtifactSaveResult,
	ArtifactsResponse,
	ArtifactTemplateCd,
	ArtifactValidationIssue,
	ArtifactValidationResult,
	DataArtifact,
	FilePreviewResponse,
	RecordDetailResponse,
} from '@trovata/app/features/ai-artifacts/models/artifacts.model';

// ==========================================
// ATOM wire types (`/api/v1/ai-artifact`)
// ==========================================
// These mirror the JSON shapes emitted by ATOM `ArtifactServlet`. The provider translates them
// into the Angular models the shared state/components consume.

/** One artifact row from `action=artifact-list` (`handleList`). `paramJson` is NOT included by the list endpoint. */
interface AtomArtifactListRow {
	readonly artifactId: string;
	readonly templateCd: string;
	readonly title: string | null;
	readonly subtitle: string | null;
	readonly moduleCd: string | null;
	readonly createdAt: string | null;
	readonly updatedAt: string | null;
}

interface AtomArtifactListResponse {
	readonly artifacts: readonly AtomArtifactListRow[];
}

/** Single artifact from `action=artifact-load` (`handleLoad`); `paramJson` is the raw CLOB JSON STRING. */
interface AtomArtifactLoadResponse {
	readonly artifactId: string;
	readonly templateCd: string;
	readonly title: string | null;
	readonly subtitle: string | null;
	readonly moduleCd: string | null;
	readonly paramJson?: string;
	readonly createdAt: string | null;
	readonly updatedAt: string | null;
}

/** Data + metadata from `action=artifact-data` (`handleDataFetch`). */
/** `validation` block from `action=artifact-save` (`validateConfig`). */
interface AtomValidationResponse {
	readonly valid?: boolean;
	readonly issues?: readonly Record<string, unknown>[];
	readonly availableFilters?: Record<string, unknown>;
	readonly availableListingFields?: Record<string, unknown>;
	readonly availableDetailFields?: Record<string, unknown>;
}

/** `action=artifact-save` response (`handleSave`). */
interface AtomArtifactSaveResponse {
	readonly success?: boolean;
	readonly artifactId?: string;
	readonly validation?: AtomValidationResponse;
}

interface AtomArtifactDataResponse {
	readonly records?: readonly Record<string, unknown>[];
	readonly columnMap?: Record<string, unknown>;
	readonly appFields?: Record<string, unknown>;
	readonly fieldTypes?: Record<string, unknown>;
	readonly primaryKeyFields?: readonly string[];
	readonly filterFields?: Record<string, unknown>;
	readonly effectiveFilters?: readonly Record<string, unknown>[];
	readonly pagination?: Record<string, unknown> | null;
}

/**
 * TmsArtifactsDataProvider — the TMS implementation of {@link ArtifactsDataProvider}.
 *
 * Talks to ATOM `ArtifactServlet` (`/api/v1/ai-artifact`) and maps every response into the Angular
 * artifact models. Auth: the `/api/v1/ai-artifact` route passes through ATOM's `RestAuthFilter`
 * (Bearer-authenticated; sets the USER_CD/TENANT_CD request attributes the servlet reads). The
 * `AuthInterceptor` attaches the Bearer to `/api/*` URLs, so this survives a Tomcat cycle. Mirrors
 * the established TMS pattern in `TmsAgentsDataProvider` — no hand-rolled auth headers.
 *
 * Action placement (mirrors the servlet): doGet reads `action` + `artifactId` from QUERY PARAMS;
 * doPost reads `action` + the remaining fields from the JSON BODY.
 *
 * Cash-isolation: this provider is only ever resolved by `ArtifactsDataProviderRouter` when the
 * active context is TMS; the Cash path never reaches it.
 *
 * Spec lock: `documentation/implementation/system/tms-data-artifacts-migration.md` §3.2 + §5 P2.
 */
@Injectable({ providedIn: 'root' })
export class TmsArtifactsDataProvider implements ArtifactsDataProvider {
	// ATOM action identifiers.
	private static readonly ACTION_LIST: string = 'artifact-list';
	private static readonly ACTION_LOAD: string = 'artifact-load';
	private static readonly ACTION_DATA: string = 'artifact-data';
	private static readonly ACTION_SAVE: string = 'artifact-save';
	private static readonly ACTION_DELETE: string = 'artifact-delete';
	private static readonly ACTION_RECORD_DETAIL: string = 'record-detail';
	private static readonly ACTION_FILE_DOWNLOAD: string = 'file-download';
	private static readonly ACTION_FILE_PREVIEW: string = 'file-preview';

	// ATOM template-cd values (server-authoritative).
	private static readonly TEMPLATE_FILE_STORAGE: string = 'file-storage';
	private static readonly TEMPLATE_AGENT_CONFIG: string = 'agent-config';

	private readonly baseUrl: string;

	constructor(
		private readonly http: HttpClient,
		@Inject(ENVIRONMENT_TOKEN) private readonly environment: DefaultEnvironment
	) {
		this.baseUrl = `${this.environment.atomApiUrl}/api/v1/ai-artifact`;
	}

	// ==========================================
	// Read operations
	// ==========================================

	listArtifacts(): Observable<ArtifactsResponse> {
		const params: HttpParams = new HttpParams().set('action', TmsArtifactsDataProvider.ACTION_LIST);
		return this.http.get<AtomArtifactListResponse>(this.baseUrl, { params, withCredentials: true }).pipe(
			map(
				(response: AtomArtifactListResponse): ArtifactsResponse => ({
					artifacts: (response.artifacts ?? []).map((row: AtomArtifactListRow) => this.atomListRowToArtifact(row)),
				})
			)
		);
	}

	loadArtifact(artifactId: string): Observable<DataArtifact> {
		const params: HttpParams = new HttpParams().set('action', TmsArtifactsDataProvider.ACTION_LOAD).set('artifactId', artifactId);
		return this.http
			.get<AtomArtifactLoadResponse>(this.baseUrl, { params, withCredentials: true })
			.pipe(map((response: AtomArtifactLoadResponse): DataArtifact => this.atomLoadToArtifact(response)));
	}

	getArtifactData(artifactId: string, paramJsonOverride?: Record<string, unknown>): Observable<ArtifactDataResponse> {
		// Edit preview supplies an override config → POST so the server runs it; otherwise GET the stored config.
		const data$: Observable<AtomArtifactDataResponse> = paramJsonOverride
			? this.http.post<AtomArtifactDataResponse>(
					this.baseUrl,
					{ action: TmsArtifactsDataProvider.ACTION_DATA, artifactId, paramJson: JSON.stringify(paramJsonOverride) },
					{ withCredentials: true }
				)
			: this.http.get<AtomArtifactDataResponse>(this.baseUrl, {
					params: new HttpParams().set('action', TmsArtifactsDataProvider.ACTION_DATA).set('artifactId', artifactId),
					withCredentials: true,
				});
		return data$.pipe(map((response: AtomArtifactDataResponse): ArtifactDataResponse => this.atomDataToResponse(response)));
	}

	getRecordDetail(artifactId: string, primaryKeys: Record<string, unknown>): Observable<RecordDetailResponse> {
		// Mirrors the servlet's doGet: action + artifactId + primaryKeys (JSON string) travel as query params.
		const params: HttpParams = new HttpParams()
			.set('action', TmsArtifactsDataProvider.ACTION_RECORD_DETAIL)
			.set('artifactId', artifactId)
			.set('primaryKeys', JSON.stringify(primaryKeys));
		return this.http.get<RecordDetailResponse>(this.baseUrl, { params, withCredentials: true });
	}

	fileDownload(fileId: string): Observable<HttpResponse<Blob>> {
		// `observe: 'response'` exposes Content-Disposition so the component can recover the original filename.
		const params: HttpParams = new HttpParams().set('action', TmsArtifactsDataProvider.ACTION_FILE_DOWNLOAD).set('fileId', fileId);
		return this.http.get(this.baseUrl, { params, withCredentials: true, responseType: 'blob', observe: 'response' });
	}

	filePreview(fileId: string): Observable<FilePreviewResponse> {
		const params: HttpParams = new HttpParams().set('action', TmsArtifactsDataProvider.ACTION_FILE_PREVIEW).set('fileId', fileId);
		return this.http.get<FilePreviewResponse>(this.baseUrl, { params, withCredentials: true });
	}

	// ==========================================
	// Write operations
	// ==========================================

	saveArtifact(body: ArtifactSaveBody): Observable<ArtifactSaveResult> {
		// The create path (empty artifactId) supplies templateCd/moduleCd/schemaVersion so the servlet's
		// INSERT branch reads them; the update path (existing artifactId) omits them (the servlet ignores
		// them on UPDATE), keeping the editor-Save body byte-identical to before this change.
		const requestBody: Record<string, unknown> = {
			action: TmsArtifactsDataProvider.ACTION_SAVE,
			artifactId: body.artifactId,
			title: body.title,
			subtitle: body.subtitle,
			paramJson: JSON.stringify(body.paramJson),
		};
		if (body.templateCd !== undefined) {
			requestBody['templateCd'] = body.templateCd;
		}
		if (body.moduleCd !== undefined) {
			requestBody['moduleCd'] = body.moduleCd;
		}
		if (body.schemaVersion !== undefined) {
			requestBody['schemaVersion'] = body.schemaVersion;
		}
		return this.http
			.post<AtomArtifactSaveResponse>(this.baseUrl, requestBody, { withCredentials: true })
			.pipe(
				map(
					(response: AtomArtifactSaveResponse): ArtifactSaveResult => ({
						artifactId: response.artifactId ?? body.artifactId,
						validation: this.atomValidationToResult(response.validation),
					})
				)
			);
	}

	deleteArtifact(artifactId: string): Observable<void> {
		return this.http
			.post<{ success: boolean }>(this.baseUrl, { action: TmsArtifactsDataProvider.ACTION_DELETE, artifactId }, { withCredentials: true })
			.pipe(map(() => undefined));
	}

	// ==========================================
	// ATOM → Angular transformers
	// ==========================================

	/** ATOM list row → Angular `DataArtifact` (no `paramJson`/`appName` from the list endpoint). */
	private atomListRowToArtifact(row: AtomArtifactListRow): DataArtifact {
		return {
			artifactId: row.artifactId,
			templateCd: this.normalizeTemplateCd(row.templateCd),
			title: row.title ?? '',
			subtitle: row.subtitle,
			appName: null,
			moduleCd: row.moduleCd,
			paramJson: null,
			createdAt: row.createdAt,
			updatedAt: row.updatedAt,
		};
	}

	/** ATOM load response → Angular `DataArtifact`; parses the raw `paramJson` CLOB string + derives `appName` from it. */
	private atomLoadToArtifact(response: AtomArtifactLoadResponse): DataArtifact {
		const paramJson: Record<string, unknown> | null = this.parseParamJson(response.paramJson);
		const appName: string | null = paramJson && typeof paramJson['appName'] === 'string' ? (paramJson['appName'] as string) : null;
		return {
			artifactId: response.artifactId,
			templateCd: this.normalizeTemplateCd(response.templateCd),
			title: response.title ?? '',
			subtitle: response.subtitle,
			appName,
			moduleCd: response.moduleCd,
			paramJson,
			createdAt: response.createdAt,
			updatedAt: response.updatedAt,
		};
	}

	/** ATOM `validateConfig` block → Angular `ArtifactValidationResult`; null when the response omits it. */
	private atomValidationToResult(validation: AtomValidationResponse | undefined): ArtifactValidationResult | null {
		if (!validation) {
			return null;
		}
		const issues: ArtifactValidationIssue[] = (validation.issues ?? []).map((raw: Record<string, unknown>): ArtifactValidationIssue => {
			const severity: unknown = raw['severity'];
			return {
				scope: typeof raw['scope'] === 'string' ? (raw['scope'] as string) : '',
				field: typeof raw['field'] === 'string' ? (raw['field'] as string) : '',
				severity: severity === 'HIGH' || severity === 'MEDIUM' || severity === 'LOW' ? severity : 'LOW',
				message: typeof raw['message'] === 'string' ? (raw['message'] as string) : '',
			};
		});
		return {
			valid: validation.valid ?? true,
			issues,
			availableFilters: validation.availableFilters ?? {},
			availableListingFields: validation.availableListingFields ?? {},
			availableDetailFields: validation.availableDetailFields ?? {},
		};
	}

	/** ATOM data response → Angular `ArtifactDataResponse` (defaults for any omitted block). */
	private atomDataToResponse(response: AtomArtifactDataResponse): ArtifactDataResponse {
		return {
			records: response.records ?? [],
			columnMap: response.columnMap ?? {},
			appFields: response.appFields ?? {},
			fieldTypes: response.fieldTypes ?? {},
			primaryKeyFields: response.primaryKeyFields ?? [],
			filterFields: response.filterFields ?? {},
			effectiveFilters: response.effectiveFilters ?? [],
			pagination: response.pagination ?? null,
		};
	}

	/** Parse the raw `PARAM_JSON` CLOB string into an object; null on absent/invalid JSON. */
	private parseParamJson(raw: string | undefined): Record<string, unknown> | null {
		if (!raw) {
			return null;
		}
		try {
			const parsed: unknown = JSON.parse(raw);
			return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
		} catch {
			return null;
		}
	}

	/** Narrow the server's `templateCd` string to the typed union; unknown values fall back to `data-table`. */
	private normalizeTemplateCd(templateCd: string): ArtifactTemplateCd {
		if (templateCd === TmsArtifactsDataProvider.TEMPLATE_FILE_STORAGE) {
			return 'file-storage';
		}
		if (templateCd === TmsArtifactsDataProvider.TEMPLATE_AGENT_CONFIG) {
			return 'agent-config';
		}
		return 'data-table';
	}
}
